import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  TextField,
  MenuItem,
  Stack,
  SvgIcon,
  useTheme,
  useMediaQuery,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  PlayArrow as PlayArrowIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  Download as DownloadIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Remove as RemoveIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { Plus } from '../../../assets';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../../components/shared/useUser';
import { complianceService } from '../../../app/services/api/compliance/complianceService';
import { Audit, AuditType } from '../../../pages/Compliance/Interfaces/complicanceInterfaces';
import { AuditStatus, getAuditStatusColor, getAuditStatusLabel } from '../Enums/complianceEnums';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Section } from '../../../components/ui/Section';
import { Pill } from '../../../components/shared/Pill';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import CustomPagination from '../../../components/forms/Pagination';

const MyAuditsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('complianceAudits');
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { hasPermissionTo, selectedStore } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [audits, setAudits] = useState<Audit[]>([]);
  const [auditTypes, setAuditTypes] = useState<AuditType[]>([]);
  const [page, setPage] = useState(0);
  const [downloadingPdfId, setDownloadingPdfId] = useState<number | null>(null);
  const [filters, setFilters] = useState({
    storeNumber: '',
    auditTypeId: 0,
    status: -1,
  });
  const localeLanguage = navigator.languages?.[0] || navigator.language;
  const rowsPerPage = 10;

  useEffect(() => {
    loadData();
  }, [page, selectedStore?.storeNumber]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const canViewAuditTypes = hasPermissionTo(['AuditTypesView']);
      const [auditsData, typesData] = await Promise.all([
        complianceService.audits.getAll(selectedStore?.storeNumber),
        canViewAuditTypes ? complianceService.auditTypes.getAll(false, selectedStore?.storeNumber) : Promise.resolve([]),
      ]);
      setAudits(auditsData);
      setAuditTypes(typesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.failedToLoadData'));
    } finally {
      setLoading(false);
    }
  };

  const filteredAudits = audits.filter(audit => {
    if (filters.storeNumber && !audit.storeNumber.toLowerCase().includes(filters.storeNumber.toLowerCase())) {
      return false;
    }
    if (filters.auditTypeId && audit.auditTypeId !== filters.auditTypeId) {
      return false;
    }
    if (filters.status >= 0 && audit.status !== filters.status) {
      return false;
    }
    return true;
  });

  // Sort audits: Draft first, then Submitted, then by scheduled date
  const sortedAudits = [...filteredAudits].sort((a, b) => {
    const aDraft = a.status === AuditStatus.Draft;
    const bDraft = b.status === AuditStatus.Draft;
    
    if (aDraft && !bDraft) return -1;
    if (!aDraft && bDraft) return 1;
    
    // If both have same status, sort by scheduled date
    const aDate = a.scheduledDate ? new Date(a.scheduledDate).getTime() : 0;
    const bDate = b.scheduledDate ? new Date(b.scheduledDate).getTime() : 0;
    return aDate - bDate;
  });

  const isScheduledDatePassed = (scheduledDate: string | null): boolean => {
    if (!scheduledDate) return true; // No scheduled date means it can be executed immediately
    return new Date(scheduledDate) <= new Date();
  };

  // Calculate summary stats
  const draftAudits = audits.filter(a => a.status === AuditStatus.Draft);
  const submittedAudits = audits.filter(a => a.status === AuditStatus.Submitted);

  const maxPages = () => {
    return Math.ceil(sortedAudits.length / rowsPerPage);
  };

  const paginatedAudits = sortedAudits.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  // Permission checks — role-based flows
  const canExecute = hasPermissionTo(['AuditsExecute']);
  const canReview = hasPermissionTo(['AuditReviewView']);
  const canSchedule = hasPermissionTo(['ScheduledAuditsCreate']);
  const canViewDashboard =  hasPermissionTo(['ViewAuditDashboard']);
  const canApprove = hasPermissionTo(['AuditsApprove']);
  const canAmend = hasPermissionTo(['AuditReviewAmend']);
  const canAccessReviewPage = canReview || canApprove || canAmend;

  const isExecutableStatus = (status: number) => status === AuditStatus.Draft;
  const isAuditExecutable = (audit: Audit): boolean => {
    if (audit.status === AuditStatus.Draft) return isScheduledDatePassed(audit.scheduledDate);
    return false;
  };
  const isReviewableStatus = (status: number) => status === AuditStatus.Submitted;

  // Non-auditors see Draft as "In Progress" and can't open it
  const getDisplayStatusLabel = (status: AuditStatus): string => {
    if (!canExecute && status === AuditStatus.Draft) {
      return t('history.statusInProgress');
    }
    return getAuditStatusLabel(status);
  };
  const getDisplayStatusColor = (status: AuditStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (!canExecute && status === AuditStatus.Draft) {
      return 'primary';
    }
    return getAuditStatusColor(status);
  };

  const handleRowClick = (audit: Audit) => {
    // Non-auditors cannot open Draft audits
    if (!canExecute && audit.status === AuditStatus.Draft) {
      return;
    }
    // Auditor flow: execute audits in Draft status
    if (canExecute && isExecutableStatus(audit.status) && isAuditExecutable(audit)) {
      navigate(`/compliance/audits/execute/${audit.id}`);
    }
    // All roles with review/approve/amend permission: view submitted via review page
    else if (canAccessReviewPage && isReviewableStatus(audit.status)) {
      navigate(`/compliance/audits/review/${audit.id}`);
    }
  };

  const handleDownloadPdf = async (audit: Audit, event: React.MouseEvent) => {
    event.stopPropagation();
    if (downloadingPdfId) return;
    
    setDownloadingPdfId(audit.id);
    try {
      await complianceService.audits.downloadPdf(audit.id, selectedStore?.storeNumber);
    } catch (error) {
      setError(t('history.pdfDownloadError'));
    } finally {
      setDownloadingPdfId(null);
    }
  };

  if (loading) {
    return (
      <PageArea>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{ justifyContent: 'space-between', mb: 3 }}
      >
        <PageTitle subTitle={t('history.pageSubTitle')}>
          {t('history.pageTitle')}
        </PageTitle>
        {canSchedule && (
          <Button
            variant="primary"
            size="lg"
            startIcon={<SvgIcon><Plus /></SvgIcon>}
            onClick={() => navigate('/compliance/audits/schedules')}
          >
            {t('history.scheduleNewAudit')}
          </Button>
        )}
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 5 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Summary Cards — visible based on dashboard permissions */}
      {canViewDashboard && (<Grid
        container
        spacing={3}
        mb={3}
      >
        <Grid
          item
          xs={12}
          sm={6}
        >
          <Section style={{ p: 5, mb: 5 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box flex={1}>
                <Box
                  sx={{
                    color: theme.palette.custom.gray[600],
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    mb: 1,
                    fontWeight: 500,
                  }}
                >
                  {t('history.statusDraft')}
                </Box>
                <Box sx={{ fontSize: '28px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.5 }}>
                  {draftAudits.length}
                </Box>
              </Box>
              <Box
                sx={{
                  borderRadius: '50%',
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <SvgIcon fontSize="medium">
                  <ScheduleIcon />
                </SvgIcon>
              </Box>
            </Box>
          </Section>
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
        >
          <Section style={{ p: 5, mb: 5 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box flex={1}>
                <Box
                  sx={{
                    color: theme.palette.custom.gray[600],
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    mb: 1,
                    fontWeight: 500,
                  }}
                >
                  {t('history.statusSubmitted')}
                </Box>
                <Box sx={{ fontSize: '28px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.5 }}>
                  {submittedAudits.length}
                </Box>
              </Box>
              <Box
                sx={{
                  borderRadius: '50%',
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',

                }}
              >
                <SvgIcon fontSize="medium">
                  <AssignmentIcon />
                </SvgIcon>
              </Box>
            </Box>
          </Section>
        </Grid>
      </Grid>)}

      {/* Filters */}
      <Box
        display="flex"
        gap={2}
        flexWrap="wrap"
      >
        <TextField
          label={t('common.storeNumber')}
          value={filters.storeNumber}
          onChange={(e) => {
            setFilters({ ...filters, storeNumber: e.target.value });
            setPage(0);
          }}
          size="small"
          sx={{ minWidth: 200 }}
        />
        {hasPermissionTo(['AuditTypesView']) && (
          <TextField
            select
            label={t('history.auditTypeFilter')}
            value={filters.auditTypeId}
            onChange={(e) => {
              setFilters({ ...filters, auditTypeId: Number(e.target.value) });
              setPage(0);
            }}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value={0}>{t('history.allTypes')}</MenuItem>
            {auditTypes.map((type) => (
              <MenuItem
                key={type.id}
                value={type.id}
              >
                {type.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select
          label={t('common.status')}
          value={filters.status}
          onChange={(e) => {
            setFilters({ ...filters, status: Number(e.target.value) });
            setPage(0);
          }}
          size="small"
          sx={{ minWidth: 200 }}
        >
          <MenuItem value={-1}>{t('common.allStatuses')}</MenuItem>
          <MenuItem value={0}>{t('history.statusDraft')}</MenuItem>
          <MenuItem value={3}>{t('history.statusSubmitted')}</MenuItem>
        </TextField>
      </Box>

      <Section>
        {sortedAudits.length === 0 ? (
          <EmptyTable
            bodyRowHeight={40}
            headerHeight={45}
            text={t('history.noAudits')}
            searchTerm={''}
            onClear={() => {
              setFilters({ storeNumber: '', auditTypeId: 0, status: -1 });
              setPage(0);
            }}
            onAdd={() => navigate('/compliance/audits/schedules')}
            onAddIsCreating={false}
            addItemText={t('history.scheduleNewAudit')}
            onAddIsDisabled={!canSchedule}
            withPaginationShown={false}
          />
        ) : (
          <>
            <Box
              sx={{
                p: 5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="textMD"
                  fontWeight={600}
                >
                  {t('history.pageTitle')}
                </Typography>
                <Pill text={t('history.auditCount', { count: sortedAudits.length })} />
              </Box>
            </Box>

            <TableContainer sx={{ borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
              <Table sx={{ minWidth: 1000 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('schedules.store')}</TableCell>
                    <TableCell>{t('history.auditTypeFilter')}</TableCell>
                    <TableCell>{t('schedules.template')}</TableCell>
                    <TableCell>{t('history.scheduledDate')}</TableCell>
                    <TableCell align="center">{t('common.status')}</TableCell>
                    <TableCell align="center">{t('common.score')}</TableCell>
                    <TableCell align="center">{t('history.result')}</TableCell>
                    <TableCell>{t('common.assignedTo')}</TableCell>
                    <TableCell align="right">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedAudits.map((audit) => (
                    <TableRow
                      key={audit.id}
                      onClick={() => handleRowClick(audit)}
                      sx={{
                        cursor: (!canExecute && audit.status === AuditStatus.Draft) ? 'default' : 'pointer',
                        opacity: (!canExecute && audit.status === AuditStatus.Draft) ? 0.6 : 1,
                        '&:hover': {
                          backgroundColor: (!canExecute && audit.status === AuditStatus.Draft) ? undefined : theme.palette.custom.gray[50],
                        },
                      }}
                    >
                      <TableCell>
                        <Tooltip
                          title={`${audit.storeNumber} - ${audit.storeName}`}
                          placement="top"
                        >
                          <Box>
                            <Box sx={{ fontWeight: 500 }}>{audit.storeNumber}</Box>
                            <Box
                              sx={{
                                fontSize: '0.875rem',
                                color: theme.palette.custom.gray[600],
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {audit.storeName}
                            </Box>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={audit.auditTypeName}
                          placement="top"
                        >
                          <span
                            style={{
                              display: 'block',
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {audit.auditTypeName}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={audit.templateName}
                          placement="top"
                        >
                          <span
                            style={{
                              display: 'block',
                              maxWidth: '250px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {audit.templateName}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="textSM">
                          {audit.scheduledDate ? new Date(audit.scheduledDate).toLocaleString(localeLanguage) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getDisplayStatusLabel(audit.status)}
                          color={getDisplayStatusColor(audit.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {audit.status >= AuditStatus.Submitted && audit.score !== null ? (
                          <Box>
                            <Box sx={{
                              fontWeight: 700,
                            }}>
                              {audit.score} / {audit.maxScore}
                            </Box>
                            <Box
                              sx={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                              }}
                            >
                              ({audit.percentageScore?.toFixed(1)}%)
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="textSM">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {audit.status >= AuditStatus.Submitted ? (
                          audit.hasPassed === true ? (
                            <Tooltip
                              title={t('history.green')}
                              placement="top"
                            >
                              <CheckIcon sx={{ color: 'success.main' }} />
                            </Tooltip>
                          ) : audit.hasPassed === false && audit.template?.amberScore != null && audit.percentageScore != null && audit.percentageScore >= audit.template.amberScore ? (
                            <Tooltip
                              title={t('history.amber')}
                              placement="top"
                            >
                              <WarningIcon sx={{ color: 'warning.main' }} />
                            </Tooltip>
                          ) : audit.hasPassed === false ? (
                            <Tooltip
                              title={t('history.red')}
                              placement="top"
                            >
                              <CloseIcon sx={{ color: 'error.main' }} />
                            </Tooltip>
                          ) : (
                            <RemoveIcon sx={{ color: theme.palette.custom.gray[400] }} />
                          )
                        ) : (
                          <RemoveIcon sx={{ color: theme.palette.custom.gray[400] }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={audit.assignedToUserName || t('common.unassigned')}
                          placement="top"
                        >
                          <span
                            style={{
                              display: 'block',
                              maxWidth: '150px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {audit.assignedToUserName || t('common.unassigned')}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        {canExecute && isExecutableStatus(audit.status) && (
                          <Tooltip
                            title={
                              !isAuditExecutable(audit)
                                ? t('history.notYetAvailable', {
                                  date: audit.scheduledDate
                                    ? new Date(audit.scheduledDate).toLocaleString()
                                    : '',
                                })
                                : t('history.executeAudit')
                            }
                            placement="top"
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={!isAuditExecutable(audit)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/compliance/audits/execute/${audit.id}`);
                                }}
                                sx={{ mr: 1, color: isAuditExecutable(audit) ? 'primary.main' : undefined }}
                              >
                                {isAuditExecutable(audit) ? <PlayArrowIcon /> : <ScheduleIcon />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {/* Review button for submitted audits */}
                        {canAccessReviewPage && isReviewableStatus(audit.status) && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/compliance/audits/review/${audit.id}`);
                            }}
                            title={t('history.reviewAudit')}
                            sx={{ mr: 1, color: 'warning.main' }}
                          >
                            <AssignmentIcon />
                          </IconButton>
                        )}
                        {/* Download for submitted audits */}
                        {audit.status === AuditStatus.Submitted && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleDownloadPdf(audit, e)}
                            title={t('history.downloadPdf')}
                            sx={{ mr: 1, color: 'success.main' }}
                            disabled={downloadingPdfId === audit.id}
                          >
                            {downloadingPdfId === audit.id ? <CircularProgress size={20} /> : <DownloadIcon />}
                          </IconButton>
                        )}
                        {/* View details — always show for any audit that doesn't already have an execute or review-assignment button */}
                        {!(canExecute && isReviewableStatus(audit.status)) && !(canAccessReviewPage && isReviewableStatus(audit.status)) && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/compliance/audits/review/${audit.id}`);
                            }}
                            title={t('history.viewDetails')}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        )}

                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination
              page={page}
              setPage={setPage}
              maxPages={maxPages()}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          </>
        )}
      </Section>
    </PageArea>
  );
};

export default MyAuditsPage;
